import { Badge } from "@/components/ui/Badge";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/Field";
import { Text } from "@/components/ui/Text";
import type { McpServer } from "@gram/client/models/components/mcpserver.js";
import type { RemoteSessionIssuer } from "@gram/client/models/components/remotesessionissuer.js";
import { useUserSessionIssuer } from "@gram/client/react-query/userSessionIssuer.js";
import { useMemo, useState, type ReactNode } from "react";
import { InlineEmptyState } from "@/components/inline-empty-state";
import { AuthRow } from "./AuthRow";
import { SettingsSection } from "@/components/detail/settings-section";
import { AttachRemoteIdentityProviderSheet } from "./AttachRemoteIdentityProviderSheet";
import { AuthenticationSetupActions } from "./AuthenticationSetupActions";
import { type AuthTarget, useMcpServerAuthTarget } from "./authTarget";
import { DeleteRemoteIdentityProviderDialog } from "./DeleteRemoteIdentityProviderDialog";
import { ModifyRemoteIdentityProviderSheet } from "./ModifyRemoteIdentityProviderSheet";
import { RemoteIdentityProvidersField } from "./RemoteIdentityProvidersField";
import { SharedAuthorizationServerField } from "./SharedAuthorizationServerField";
import { UserSessionIssuerField } from "./UserSessionIssuerField";
import { useSelectableUserSessionIssuers } from "./useSelectableUserSessionIssuers";
import { UserIdentitySessionControls } from "./UserIdentitySessionControls";
import {
  useAllRemoteSessionClients,
  useRemoteSessionIssuersByIds,
} from "@/lib/remote-identity";
import { RemoteMcpIdentitySectionBody } from "./RemoteMcpIdentitySection";

export const MCP_AUTHENTICATION_SECTION_ID = "authentication";

function authenticationSectionDescription(
  isUnproxied: boolean,
  isRemoteMcp: boolean,
  upstreamName: string,
): string {
  if (isUnproxied) {
    return "Speakeasy doesn't manage authentication for unproxied servers.";
  }
  if (isRemoteMcp) {
    return `How callers are identified to ${upstreamName}. Changes take effect on new connections.`;
  }
  return "Who may connect to this server and how they sign in. Changes take effect on new connections.";
}

/**
 * Chrome wrapper for the remote/tunneled MCP server settings tab. The
 * target-agnostic body below also mounts on the toolset detail page inside
 * that page's own section chrome.
 */
export function AuthenticationSection({
  mcpServer,
  mcpUrl,
}: {
  mcpServer: McpServer;
  /** The URL clients connect to; undefined while it is unknown. */
  mcpUrl?: string;
}): JSX.Element {
  const isUnproxied = !!mcpServer.unproxiedMcpServerId;
  const isRemoteMcp = !!mcpServer.remoteMcpServerId;
  const target = useMcpServerAuthTarget(mcpServer, mcpUrl);

  return (
    <SettingsSection id={MCP_AUTHENTICATION_SECTION_ID}>
      <SettingsSection.Header>
        <SettingsSection.Title>
          {isRemoteMcp ? "Identity" : "Authentication"}
        </SettingsSection.Title>
        <SettingsSection.Description>
          {authenticationSectionDescription(
            isUnproxied,
            isRemoteMcp,
            mcpServer.name?.trim() || "the upstream service",
          )}
        </SettingsSection.Description>
      </SettingsSection.Header>
      {isUnproxied ? (
        <SettingsSection.Panel>
          <SettingsSection.Body>
            <UnproxiedAuthenticationNotice />
          </SettingsSection.Body>
        </SettingsSection.Panel>
      ) : (
        <AuthenticationSectionBody target={target} />
      )}
    </SettingsSection>
  );
}

function UnproxiedAuthenticationNotice(): JSX.Element {
  return (
    <Field>
      <FieldLabel>Authentication</FieldLabel>
      <div>
        <Badge variant="success">Not applicable</Badge>
      </div>
      <FieldDescription>
        The customer connects directly using the vendor&apos;s own credentials —
        there&apos;s nothing for Speakeasy to configure here.
      </FieldDescription>
    </Field>
  );
}

/**
 * The auth configuration surface: identity-provider setup or the manage
 * rows, plus the attach/modify/delete overlays. It owns its own panel (the
 * rows share one bordered surface and its dividers) but not the section
 * heading, so each shell supplies only the header above it.
 */
export function AuthenticationSectionBody({
  target,
  additionalSetupAction,
}: {
  target: AuthTarget;
  additionalSetupAction?: ReactNode;
}): JSX.Element {
  if (target.kind === "remote-mcp") {
    // Keyed by server: the panel holds unsaved header and identity drafts,
    // and navigating to another server must not carry them across for Save
    // to write to the wrong remote. Every render site dispatches through here.
    return (
      <RemoteMcpIdentitySectionBody
        key={`${target.permissionResourceId}:${target.remoteMcpServerId ?? ""}`}
        target={target}
      />
    );
  }

  return (
    <StandardAuthenticationSectionBody
      target={target}
      additionalSetupAction={additionalSetupAction}
    />
  );
}

function StandardAuthenticationSectionBody({
  target,
  additionalSetupAction,
}: {
  target: AuthTarget;
  additionalSetupAction?: ReactNode;
}): JSX.Element {
  const userSessionIssuerId = target.userSessionIssuerId ?? undefined;
  const issuerConfigured = !!userSessionIssuerId;
  const effectiveIssuersQuery = useSelectableUserSessionIssuers(target);

  const {
    data: userSessionIssuer,
    isLoading: isLoadingUserSessionIssuer,
    isError: isUserSessionIssuerError,
  } = useUserSessionIssuer({ id: userSessionIssuerId }, undefined, {
    enabled: issuerConfigured,
  });

  const {
    items: allClients,
    isLoading: isLoadingClients,
    isError: isClientsError,
  } = useAllRemoteSessionClients(
    { userSessionIssuerId },
    { enabled: issuerConfigured },
  );

  const attachedIssuerIds = useMemo(
    () => [
      ...new Set(allClients.map((client) => client.remoteSessionIssuerId)),
    ],
    [allClients],
  );

  // Resolved by id, never by filtering the issuer listing: an attached issuer
  // can fall past any listing page, and filtering would then render a working
  // server as having no provider.
  const {
    items: associatedIssuers,
    isLoading: isLoadingAssociatedIssuers,
    isError: isAssociatedIssuersError,
  } = useRemoteSessionIssuersByIds(attachedIssuerIds, {
    enabled: issuerConfigured,
  });

  const [sheetOpen, setSheetOpen] = useState(false);

  // Keep targets mounted for one render after close so exit animations retain
  // the row that triggered them.
  const [deleteTarget, setDeleteTarget] = useState<RemoteSessionIssuer | null>(
    null,
  );
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [modifyTarget, setModifyTarget] = useState<RemoteSessionIssuer | null>(
    null,
  );
  const [modifyOpen, setModifyOpen] = useState(false);

  const handleEdit = (issuer: RemoteSessionIssuer) => {
    setModifyTarget(issuer);
    setModifyOpen(true);
  };

  const handleDelete = (issuer: RemoteSessionIssuer) => {
    setDeleteTarget(issuer);
    setDeleteOpen(true);
  };

  let authenticationFields: ReactNode;
  if (!issuerConfigured) {
    authenticationFields = (
      <IdentityProviderSetupField
        onStartManual={() => setSheetOpen(true)}
        additionalAction={additionalSetupAction}
      />
    );
  } else if (isLoadingUserSessionIssuer) {
    authenticationFields = <AuthenticationLoadingField />;
  } else if (isUserSessionIssuerError || !userSessionIssuer) {
    authenticationFields = <AuthenticationLoadErrorField />;
  } else {
    authenticationFields = (
      <>
        <UserIdentitySessionControls userSessionIssuer={userSessionIssuer} />
        <SharedAuthorizationServerField
          userSessionIssuer={userSessionIssuer}
          mcpUrl={target.mcpUrl}
        />
        <RemoteIdentityProvidersField
          associatedIssuers={associatedIssuers}
          allowAdditionalProviders={!!target.multipleProviders}
          projectId={target.projectId}
          isLoading={isLoadingClients || isLoadingAssociatedIssuers}
          isError={isClientsError || isAssociatedIssuersError}
          onAdd={() => setSheetOpen(true)}
          onEdit={handleEdit}
          onDelete={handleDelete}
          readOnly={userSessionIssuer.projectId === ""}
        />
      </>
    );
  }

  return (
    <>
      {/* No footer hint: the section description above already says these
          changes take effect on new connections. */}
      <SettingsSection.Panel>
        <div className="divide-y">
          <UserSessionIssuerField
            target={target}
            issuers={effectiveIssuersQuery.issuers}
            isLoading={effectiveIssuersQuery.isLoading}
            isError={effectiveIssuersQuery.isError}
          />
          {authenticationFields}
        </div>
      </SettingsSection.Panel>

      <AttachRemoteIdentityProviderSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        target={target}
        userSessionIssuer={userSessionIssuer ?? null}
        excludedIssuerIds={attachedIssuerIds}
      />

      {deleteTarget && userSessionIssuerId && (
        <DeleteRemoteIdentityProviderDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          userSessionIssuerId={userSessionIssuerId}
          issuer={deleteTarget}
        />
      )}

      {modifyTarget && userSessionIssuer && (
        <ModifyRemoteIdentityProviderSheet
          open={modifyOpen}
          onOpenChange={setModifyOpen}
          userSessionIssuer={userSessionIssuer}
          issuer={modifyTarget}
        />
      )}
    </>
  );
}

function IdentityProviderSetupField({
  onStartManual,
  additionalAction,
}: {
  onStartManual: () => void;
  additionalAction?: ReactNode;
}) {
  return (
    <AuthRow
      label="Identity provider"
      hint="Nobody can be identified here until a provider vouches for them."
    >
      <InlineEmptyState
        icon="key-round"
        heading="Set up authentication"
        description="Require MCP clients to authenticate through an upstream identity provider before reaching this server."
        className="py-8"
        action={
          <AuthenticationSetupActions
            onStartManual={onStartManual}
            additionalAction={additionalAction}
          />
        }
      />
    </AuthRow>
  );
}

function AuthenticationLoadingField() {
  return (
    <AuthRow label="Authentication">
      <Text muted small>
        Loading…
      </Text>
    </AuthRow>
  );
}

function AuthenticationLoadErrorField() {
  return (
    <AuthRow label="Authentication">
      <FieldError>
        Failed to load the authentication configuration. Refresh the page to try
        again.
      </FieldError>
    </AuthRow>
  );
}
